import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { secureFetch } from "../data/secureFetch";
import "./AcceptInvite.css";

/**
 * Accept Invite Page
 * Route: /accept-invite?token=...
 * Accepts organization membership invite and redirects to login/dashboard
 */
function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Processing your invitation...");
  const [orgInfo, setOrgInfo] = useState(null);

  useEffect(() => {
    const acceptInvitation = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setMessage("Invalid invitation link. No token provided.");
        return;
      }

      try {
        // Call backend to accept invite
        const response = await secureFetch("/api/memberships/accept", {
          method: "POST",
          data: { token },
        });

        if (response.ok) {
          setStatus("success");
          setMessage("Invitation accepted successfully!");
          setOrgInfo({
            organizationId: response.organizationId,
            organizationName: response.organizationName,
            role: response.role,
          });

          // Store org info for post-login redirect
          localStorage.setItem("pendingOrgId", response.organizationId);
          localStorage.setItem("pendingOrgName", response.organizationName);

          // Check if user is already logged in
          const auth = getAuth();
          if (auth.currentUser) {
            // Already logged in, redirect to dashboard
            setTimeout(() => {
              navigate("/dashboard");
            }, 2000);
          } else {
            // Not logged in, redirect to login
            setTimeout(() => {
              navigate("/login");
            }, 2000);
          }
        } else {
          setStatus("error");
          setMessage(response.error || "Failed to accept invitation");
        }
      } catch (error) {
        console.error("Error accepting invitation:", error);
        setStatus("error");
        setMessage("An error occurred while processing your invitation");
      }
    };

    acceptInvitation();
  }, [searchParams, navigate]);

  return (
    <div className="accept-invite-page">
      <div className="accept-invite-container">
        <div className={`status-icon ${status}`}>
          {status === "processing" && <div className="spinner"></div>}
          {status === "success" && <span className="success-checkmark">✓</span>}
          {status === "error" && <span className="error-icon">✕</span>}
        </div>

        <h1 className="accept-invite-title">
          {status === "processing" && "Processing Invitation"}
          {status === "success" && "Invitation Accepted!"}
          {status === "error" && "Invitation Error"}
        </h1>

        <p className="accept-invite-message">{message}</p>

        {status === "success" && orgInfo && (
          <div className="org-info">
            <p>
              <strong>Organization:</strong> {orgInfo.organizationName}
            </p>
            <p>
              <strong>Role:</strong> {orgInfo.role}
            </p>
            <p className="redirect-message">Redirecting you to login...</p>
          </div>
        )}

        {status === "error" && (
          <div className="error-actions">
            <button onClick={() => navigate("/login")} className="btn-primary">
              Go to Login
            </button>
            <button onClick={() => navigate("/")} className="btn-secondary">
              Go to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AcceptInvite;
